import React, { useState, useEffect, useCallback, useMemo } from "react";
import propTypes from "prop-types";
import { Sidebar } from "../sidebar/Sidebar";
import {
  Button,
  makeStyles,
  Card,
  CardPreview,
  mergeClasses,
  Spinner,
  tokens,
  Badge
} from "@fluentui/react-components";
import { ArrowShuffleFilled, ChevronDown24Regular } from "@fluentui/react-icons";
import { FormattedMessage, useIntl } from "react-intl";
import { AvatarBuilderApi } from "../../api/AvatarBuilderApi";
import { proxiedUrlFor } from "../../utils/media-url-utils";
import { AvatarHangerAssets, mainCategories } from "./AvatarHangerAssets";
import useBreakpoints from "../hooks/useBreakpoints";

const FormatTag = tag => {
  return tag.replace(/[-_]/g, " ").replace(/\b\w/g, l => l.toUpperCase());
};

const useClasses = makeStyles({
  tagsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalM,
    padding: "0 4px",
    justifyContent: "center"
  },
  tagBadge: {
    // Rename from tag to tagBadge to match source semantics/naming if preferred, or just style Badge
    minWidth: "70px",
    cursor: "pointer",
    transition: "transform 0.1s ease-in-out",
    "&:hover": {
      transform: "scale(1.05)"
    },
    "&:active": {
      transform: "scale(0.95)"
    }
  },
  container: {
    display: "flex",
    overflow: "auto",
    gap: "10px",
    paddingBottom: "10px",
    "@media (min-width: 992px)": {
      gap: "15px",
      display: "grid",
      gridTemplateColumns: "1fr 1fr"
    }
  },
  card: {
    cursor: "pointer",
    width: "150px",
    height: "150px",
    minWidth: "150px",
    minHeight: "150px",
    background: "var(--primaryColorBackground)",
    aspectRatio: "1 / 1",
    "& img": {
      objectFit: "cover",
      aspectRatio: "1 / 1",
      width: "100%",
      height: "100%"
    },
    "@media (min-width: 992px)": {
      width: "200px",
      height: "200px",
      maxHeight: "200px"
    }
  },
  sidebar: {
    "&.fui-Drawer": {
      overflow: "initial",
      background: "var(--colorNeutralBackground1)",
      height: "0",
      width: "100dvw",
      opacity: 0,
      transition: "height 0.2s ease-in-out, width 0.2s ease-in-out, opacity 0.2s ease-in-out",
      "& .fui-DrawerBody": {
        background: "transparent",
        overflow: "visible"
      },
      "@media (min-width: 992px)": {
        width: "480px",
        minWidth: "480px",
        height: "100%",
        opacity: 1,
        "& .fui-DrawerBody": {
          overflow: "auto"
        }
      }
    }
  },
  sidebarOpen: {
    "&.fui-Drawer": {
      height: "auto",
      opacity: 1,
      "@media (min-width: 992px)": {
        height: "100%"
      }
    }
  },
  header: {
    display: "none",
    backgroundColor: "transparent !important",
    "@media (min-width: 992px)": {
      display: "flex"
    }
  },
  content: {
    padding: "20px 20px 10px",
    overflow: "auto",
    "@media (min-width: 992px)": {
      padding: "24px"
    }
  },
  customizeAvatar: {
    padding: "0",
    paddingTop: tokens.spacingVerticalMNudge,
    paddingBottom: "0!important",
    "@media (min-width: 992px)": {
      padding: "24px",
      paddingBottom: "24px!important",
      paddingTop: "24px"
    }
  },
  disabled: {
    pointerEvents: "none",
    opacity: 0.75
  },
  footer: {
    padding: "10px 20px",
    "@media (min-width: 992px)": {
      padding: "24px"
    }
  },
  mobileCloseButton: {
    position: "absolute",
    bottom: "100%",
    left: "20px",
    width: "45px",
    height: "45px",
    zIndex: 100,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderTopRightRadius: tokens.borderRadiusMedium,
    borderTopLeftRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    transition: "bottom 0.2s ease-in-out",
    "& svg": {
      transition: "transform 0.2s",
      transform: "rotate(180deg)"
    },
    "@media (min-width: 992px)": {
      display: "none"
    }
  },
  mobileCloseButtonOpen: {
    "& svg": {
      transform: "rotate(0)"
    }
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    height: "100%"
  }
});

// const rpmSubdomain = configs.RPM_SUBDOMAIN;
// const rpmApplicationId = configs.RPM_APPLICATION_ID;

const COLOR_CATEGORY_API_MAP = {
  hair: "hair",
  faceshape: "skin",
  eyebrow: "eyebrow",
  beard: "beard"
};

const NOT_ZOOMABLE_CATEGORIES = ["avatar", "outfit"];
const FEET_ZOOMABLE_CATEGORIES = ["footwear"];

export function AvatarHanger({
  disabled = false,
  step,
  setStep,
  STEPS,
  onAvatarChange = () => {},
  zoomToHead = () => {},
  zoomToFeet = () => {},
  resetZoom = () => {},
  resetFeetZoom = () => {},
  onEquipLoadingChange = () => {}
}) {
  const classes = useClasses();

  // const [rpmUser, setRpmUser] = useState(null);
  const [selectedMainCategory, setSelectedMainCategory] = useState("outfit");

  const [draftAvatar, setDraftAvatar] = useState(null);
  const [descriptor, setDescriptor] = useState({});
  const [categories, setCategories] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState({});
  const [loading, setLoading] = useState("");
  const [error, setError] = useState("");
  const [categorizedAssets, setCategorizedAssets] = useState({});
  const [category, setCategory] = useState("top");
  const [colors, setColors] = useState({});

  const title = step === STEPS.SELECT_TEMPLATE ? "Select a base template" : "Customize your avatar";

  const [presets, setPresets] = useState([]);
  const [selectedTags, setSelectedTags] = useState(["All"]);

  // Extract unique tags and always include "All"
  const availableTags = useMemo(() => {
    const tags = new Set(["All"]);
    presets.forEach(p => {
      if (Array.isArray(p.tags)) {
        p.tags.forEach(t => tags.add(t));
      }
    });
    return Array.from(tags).sort((a, b) => {
      if (a === "All") return -1;
      if (b === "All") return 1;
      return a.localeCompare(b);
    });
  }, [presets]);

  const filteredPresets = useMemo(() => {
    if (selectedTags.includes("All")) {
      return presets;
    }
    return presets.filter(p => {
      if (!p.tags) return false;
      return selectedTags.some(tag => p.tags.includes(tag));
    });
  }, [presets, selectedTags]);

  const toggleTag = tag => {
    if (tag === "All") {
      setSelectedTags(["All"]);
    } else {
      setSelectedTags(prev => {
        let newTags = prev.filter(t => t !== "All");
        if (newTags.includes(tag)) {
          newTags = newTags.filter(t => t !== tag);
        } else {
          newTags = [...newTags, tag];
        }
        if (newTags.length === 0) {
          return ["All"];
        }
        return newTags;
      });
    }
  };

  useEffect(() => {
    setLoading("presets");
    AvatarBuilderApi.fetchPresets()
      .then(fetchedPresets => {
        // Deduplicate
        const unique = [];
        const seen = new Set();
        (fetchedPresets || []).forEach(p => {
          if (!seen.has(p.id)) {
            seen.add(p.id);
            unique.push(p);
          }
        });
        setPresets(unique);
        setError("");
      })
      .catch(err => {
        console.error("Error fetching presets:", err);
        setError(`Presets Error: ${err.message}`);
      })
      .finally(() => setLoading(""));
  }, []);

  const formatAssets = options => {
    const result = {};
    for (const [key, items] of Object.entries(options)) {
      if (Array.isArray(items)) {
        result[key] = items.map(item => ({ ...item, iconUrl: item.thumbnail }));
      } else {
        result[key] = items; // e.g. skinTones
      }
    }
    return result;
  };

  // Keys that are metadata/control fields, not equippable asset categories
  const NON_ASSET_KEYS = new Set([
    "avatarType",
    "avatarId",
    "bodyWeight",
    "muscle",
    "skinColor",
    "faceMorphs",
    "metadata",
    "head",
    "armature"
  ]);

  const buildSelectedAssets = source => {
    const assets = {};
    for (const [key, value] of Object.entries(source)) {
      if (!NON_ASSET_KEYS.has(key) && typeof value === "string") {
        assets[key] = value;
      }
    }
    return assets;
  };

  const doFetchAllAssets = useCallback(async () => {}, []);

  const handleCategoryChange = useCallback(async categoryId => {
    setCategory(categoryId);
  }, []);

  const handleMainCategoryChange = useCallback(
    async categoryId => {
      setSelectedMainCategory(categoryId);

      const allCategories = categories.length > 0 ? categories : mainCategories;
      const mainCategory = allCategories.find(cat => cat.id === categoryId);
      const accessories = mainCategory?.subcategories || mainCategory?.accessories || [];

      if (accessories.length > 0) {
        let defaultAccessoryId = accessories[0].id;

        if (categoryId === "faceshape" && descriptor) {
          // If we have eye colors, prefer eyeshape
          // Check for both backend ID ('eyes') and frontend ID ('eyeshape')
          if (categorizedAssets.eyes?.length > 0) {
            const eyes = accessories.find(acc => acc.id === "eyeshape" || acc.id === "eyes");
            if (eyes) defaultAccessoryId = eyes.id;
          }
        }

        setCategory(defaultAccessoryId);
      } else {
        setCategory("");
      }

      // Only show skin color picker if this avatar type supports it (feature flag)
      const newColors = {};

      if (
        categoryId === "faceshape" &&
        descriptor &&
        categorizedAssets.features?.skinColorPicker !== false &&
        categorizedAssets.skinTones?.length > 0
      ) {
        newColors.faceshape = categorizedAssets.skinTones.map(tone => ({
          id: tone.color,
          color: `rgb(${tone.color[0]}, ${tone.color[1]}, ${tone.color[2]})`,
          type: "skinColor",
          label: tone.displayName
        }));
      }

      if (categoryId === "faceshape" && descriptor && categorizedAssets.eyes?.length > 0) {
        newColors.eyeshape = categorizedAssets.eyes.map(eye => ({
          id: eye.id,
          iconUrl: eye.iconUrl,
          type: "eyes",
          label: eye.displayName
        }));
      }

      setColors(newColors);

      if (NOT_ZOOMABLE_CATEGORIES.includes(categoryId)) {
        resetZoom();
      } else {
        zoomToHead();
      }
    },
    [categories, categorizedAssets, descriptor, resetZoom, zoomToHead]
  );

  const handleCreateDraftAvatar = useCallback(async () => {}, []); // Handled in handleSelectPreset for now

  const handleAvatarTypeChange = useCallback(async () => {}, []);

  const handleEquipAsset = useCallback(
    async (assetType, assetId) => {
      // 1. Update selectedAssets (The UI State with memory)
      const newAssets = { ...selectedAssets };

      onEquipLoadingChange(true);

      if (assetId) {
        newAssets[assetType] = assetId;
      } else {
        delete newAssets[assetType];
      }

      // Enforce exclusivity: Selecting a piece removes the overall
      if (["top", "bottom", "footwear"].includes(assetType) && assetId) {
        delete newAssets.overall;
      }
      // Note: If selecting overall, we intentionally keep top/bottom/footwear in newAssets
      // to preserve the state for when the user switches back.

      setSelectedAssets(newAssets);

      // Special handling for bodyShape (morphs)
      if (assetType === "bodyShape") {
        const fallbackWeightMap = {
          average: { bodyWeight: 0, muscle: 0 },
          athletic: { bodyWeight: 0, muscle: 1 },
          plussize: { bodyWeight: 0.85, muscle: 0 }
        };
        // different structure in this codebase potentially, but let's assume categorizedAssets contains it or we use fallback
        const preset = categorizedAssets.bodyShapePresets?.find(p => p.id === assetId);
        const config = preset?.morphs || fallbackWeightMap[assetId] || fallbackWeightMap.average;

        const finalDescriptor = {
          ...descriptor,
          bodyWeight: config.bodyWeight,
          muscle: config.muscle
        };
        // Remove bodyShape from descriptor if it got there somehow, though strictly speaking it's just not added.
        delete finalDescriptor.bodyShape;

        setDescriptor(finalDescriptor);

        // Update UI state
        setSelectedAssets(prev => ({ ...prev, [assetType]: assetId }));

        const newDraft = { ...draftAvatar, descriptor: finalDescriptor };
        setDraftAvatar(newDraft);
        onAvatarChange(newDraft); // Sync immediately for preview if handled by parent, or local generate below

        setLoading("preview");
        try {
          const blob = await AvatarBuilderApi.generateAvatar(finalDescriptor);
          const previewUrl = URL.createObjectURL(blob);
          const updatedDraft = { ...newDraft, previewUrl, preserveCamera: true };
          setDraftAvatar(updatedDraft);
          onAvatarChange(updatedDraft);
        } catch (err) {
          console.error("Preview generation failed", err);
        } finally {
          setLoading("");
          onEquipLoadingChange(false);
        }
        return;
      }

      // 2. Update descriptor (The Backend Payload)
      // We must ensure the backend doesn't receive conflicting assets
      const newDescriptor = { ...descriptor, ...newAssets };

      if (newAssets.overall) {
        // If overall is equipped, it suppresses pieces
        delete newDescriptor.top;
        delete newDescriptor.bottom;
        delete newDescriptor.footwear;
      } else {
        // If overall is NOT equipped (or removed), ensure it's gone from descriptor
        delete newDescriptor.overall;
      }

      // Handle explicit un-equip (e.g. clicking the remove button)
      if (!assetId) {
        delete newDescriptor[assetType];
      }

      setDescriptor(newDescriptor);

      const newDraft = { ...draftAvatar, descriptor: newDescriptor };
      setDraftAvatar(newDraft);

      setLoading("preview");
      try {
        const blob = await AvatarBuilderApi.generateAvatar(newDescriptor);
        const previewUrl = URL.createObjectURL(blob);
        setDraftAvatar({ ...newDraft, previewUrl, preserveCamera: true });
        onAvatarChange({ ...newDraft, previewUrl, preserveCamera: true });
      } catch (err) {
        console.error("Preview generation failed", err);
      } finally {
        setLoading("");
        onEquipLoadingChange(false);
      }
    },
    [descriptor, draftAvatar, onAvatarChange, selectedAssets, onEquipLoadingChange, categorizedAssets.bodyShapePresets]
  );

  const draftAvatarType = draftAvatar?.avatarType;
  const onRandomizeAvatar = useCallback(async () => {
    setLoading("randomize");
    onEquipLoadingChange(true);

    try {
      const avatarType = draftAvatarType || (Math.random() > 0.5 ? "male" : "female");
      const randomDescriptor = await AvatarBuilderApi.getRandomDescriptor(avatarType);
      setDescriptor(randomDescriptor);

      const blob = await AvatarBuilderApi.generateAvatar(randomDescriptor);
      const previewUrl = URL.createObjectURL(blob);

      // Only fetch options if we don't have a draft avatar type yet (initialization)
      if (!draftAvatarType) {
        const options = await AvatarBuilderApi.fetchOptions(avatarType);
        setCategorizedAssets(formatAssets(options));

        // Fetch categories dynamically
        try {
          const cats = await AvatarBuilderApi.fetchCategories(avatarType);
          setCategories(cats);
        } catch (e) {
          console.error("Failed to fetch categories", e);
        }
      }

      // Map back to bodyShape key for UI
      let bodyShape = "average";
      if (randomDescriptor.muscle === 1) {
        bodyShape = "athletic";
      } else if (randomDescriptor.bodyWeight === 1) {
        bodyShape = "plussize";
      }

      setSelectedAssets({
        ...buildSelectedAssets(randomDescriptor),
        bodyShape
      });

      if (step === STEPS.SELECT_TEMPLATE) {
        setSelectedMainCategory("outfit");
        setCategory("top");
        setStep(STEPS.CUSTOMIZE_AVATAR);
      }

      const avatar = {
        avatarType,
        previewUrl,
        descriptor: randomDescriptor
      };
      setDraftAvatar(avatar);
      onAvatarChange(avatar);
    } catch (err) {
      console.error("Error randomizing avatar:", err);
    } finally {
      setLoading("");
      onEquipLoadingChange(false);
    }
  }, [
    step,
    STEPS.SELECT_TEMPLATE,
    STEPS.CUSTOMIZE_AVATAR,
    draftAvatarType,
    onAvatarChange,
    setStep,
    onEquipLoadingChange
  ]);

  const handleSelectPreset = useCallback(
    async (avatarType, presetTemplate = null) => {
      setLoading("preset");

      try {
        const preset = presetTemplate
          ? { ...presetTemplate, avatarType }
          : await AvatarBuilderApi.fetchBase(avatarType);

        setDescriptor(preset);

        const blob = await AvatarBuilderApi.generateAvatar(preset);
        const previewUrl = URL.createObjectURL(blob);

        const options = await AvatarBuilderApi.fetchOptions(avatarType);
        setCategorizedAssets(formatAssets(options));

        // Fetch categories dynamically
        try {
          const cats = await AvatarBuilderApi.fetchCategories(avatarType);
          setCategories(cats);
        } catch (e) {
          console.error("Failed to fetch categories", e);
        }

        setSelectedAssets(buildSelectedAssets(preset));

        setSelectedMainCategory("outfit");
        setCategory("top");

        const avatar = {
          avatarType,
          previewUrl,
          descriptor: preset
        };
        setDraftAvatar(avatar);
        onAvatarChange(avatar);
        setStep(STEPS.CUSTOMIZE_AVATAR);
      } catch (err) {
        console.error("Error selecting preset:", err);
        setError(`Select Preset Error: ${err.message}`);
      } finally {
        setLoading("");
      }
    },
    [onAvatarChange, setStep, STEPS.CUSTOMIZE_AVATAR]
  );

  const onRandomizeFromPresets = useCallback(() => {
    if (presets.length === 0) return;
    const randomPreset = presets[Math.floor(Math.random() * presets.length)];
    handleSelectPreset(randomPreset.avatarType || randomPreset.gender || "mike", randomPreset);
  }, [presets, handleSelectPreset]);

  const breakpoint = useBreakpoints();
  const isMobile = ["xs", "sm", "md"].includes(breakpoint);
  const [isOpen, setIsOpen] = useState(true);
  const intl = useIntl();

  const [rootElement, setRootElement] = useState(null);

  return (
    <>
      <Sidebar
        title={title}
        position={isMobile ? "bottom" : "end"}
        type={isMobile ? "overlay" : "inline"}
        headerClassName={classes.header}
        contentClassName={mergeClasses(classes.content, step === STEPS.CUSTOMIZE_AVATAR && classes.customizeAvatar)}
        footer={
          step === STEPS.SELECT_TEMPLATE ? (
            <Button
              appearance="primary"
              onClick={onRandomizeFromPresets}
              style={{ width: "100%" }}
              icon={<ArrowShuffleFilled />}
            >
              <FormattedMessage id="avatar-hanger.random-avatar" defaultMessage="Random Avatar" />
            </Button>
          ) : null
        }
        footerClassName={classes.footer}
        className={mergeClasses(classes.sidebar, disabled && classes.disabled, isOpen && classes.sidebarOpen)}
      >
        <div
          role="button"
          className={mergeClasses(classes.mobileCloseButton, isOpen && classes.mobileCloseButtonOpen)}
          onClick={() => setIsOpen(!isOpen)}
          aria-label={intl.formatMessage({ id: "sidebar.close", defaultMessage: "Close component" })}
        >
          <ChevronDown24Regular />
        </div>

        {step === STEPS.SELECT_TEMPLATE && (
          <>
            {loading === "preset" ? (
              <div className={classes.loadingContainer}>
                <Spinner label={<FormattedMessage id="avatar-hanger.creating-avatar" defaultMessage="Creating avatar..." />} />
              </div>
            ) : loading === "presets" ? (
              <div className={classes.loadingContainer}>
                <Spinner
                  label={
                    <FormattedMessage id="avatar-hanger.loading-templates" defaultMessage="Loading templates..." />
                  }
                />
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                <div className={classes.tagsContainer}>
                  <Badge
                    className={classes.tagBadge}
                    appearance={selectedTags.includes("All") ? "filled" : "outline"}
                    color={selectedTags.includes("All") ? "brand" : "neutral"}
                    onClick={() => toggleTag("All")}
                    size="large"
                  >
                    {intl.formatMessage({ id: "avatar.tag.all", defaultMessage: "All" })}
                  </Badge>
                  {availableTags
                    .filter(t => t !== "All")
                    .map(tag => (
                      <Badge
                        key={tag}
                        className={classes.tagBadge}
                        appearance={selectedTags.includes(tag) ? "filled" : "outline"}
                        color={selectedTags.includes(tag) ? "brand" : "neutral"}
                        onClick={() => toggleTag(tag)}
                        size="large"
                      >
                        {FormatTag(tag)}
                      </Badge>
                    ))}
                </div>
                <div className={classes.container}>
                  {filteredPresets.map((preset, i) => (
                    <Card
                      key={preset.id || i}
                      className={classes.card}
                      role="button"
                      onClick={() => handleSelectPreset(preset.avatarType || "mike", preset)}
                    >
                      <CardPreview>
                        {preset.thumbnail ? (
                          <img src={proxiedUrlFor(preset.thumbnail)} alt={preset.displayName} />
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              height: "100%",
                              color: "white"
                            }}
                          >
                            {preset.displayName}
                          </div>
                        )}
                      </CardPreview>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {step === STEPS.CUSTOMIZE_AVATAR && draftAvatar && (
          <AvatarHangerAssets
            assets={categorizedAssets}
            handleEquipAsset={handleEquipAsset}
            handleAvatarTypeChange={handleAvatarTypeChange}
            equippedAssets={selectedAssets}
            onRandomizeAvatar={onRandomizeAvatar}
            avatarType={draftAvatar.avatarType}
            onCategoryChange={handleCategoryChange}
            selectedCategory={category}
            colors={colors}
            selectedMainCategory={selectedMainCategory}
            onMainCategoryChange={handleMainCategoryChange}
            loading={loading}
            isMobile={isMobile}
            avatarEditorRef={rootElement}
            categories={categories}
            isAvatarBuilderMode={true}
            disabledCategories={descriptor ? ["glasses", "facemask"] : []}
          />
        )}
      </Sidebar>
      <div ref={setRootElement} />
    </>
  );
}

AvatarHanger.propTypes = {
  onAvatarChange: propTypes.func,
  onError: propTypes.func,
  zoomToHead: propTypes.func,
  zoomToFeet: propTypes.func,
  resetZoom: propTypes.func,
  resetFeetZoom: propTypes.func,
  onEquipLoadingChange: propTypes.func,
  disabled: propTypes.bool,
  step: propTypes.string.isRequired,
  setStep: propTypes.func.isRequired,
  STEPS: propTypes.object.isRequired
};
